/**
 * Today's free stocks on app.ortex.com (ted #2647).
 *
 * Staff set them on /staff/api-usage/free-stocks/: dated windows, or an automatic
 * daily rotation when none is live. A free stock is unlocked for anyone signed in,
 * free accounts included; signed-out visitors still get the locked view, so the
 * LockedValuePanel asks them to sign in rather than to upgrade.
 *
 * The public `/pay/api/free_stocks/` list (the marketing ticker tape's) marks the
 * day's free set with `freeToday`. Fetched once per page load; never rejects.
 */

import { StockRef } from "@/lib/lockedPages";

export interface FreeStock {
    ticker: string;
    securityId: number;
}

let inflight: Promise<FreeStock[]> | null = null;

// The locked panel waits for this list on stock pages, so a slow answer counts as
// "nothing free" rather than holding the panel back.
const TIMEOUT_MS = 3000;

export const loadFreeStocksToday = (): Promise<FreeStock[]> => {
    if (inflight) return inflight;
    const base = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/$/, "");
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    inflight = fetch(`${base}/pay/api/free_stocks/`, { credentials: "omit", signal: controller.signal })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .finally(() => clearTimeout(timer))
        .then((data) => {
            const rows: any[] = Array.isArray(data?.rows) ? data.rows : [];
            return rows.filter((row) => row?.freeToday ?? row?.free_today).map((row) => ({ ticker: String(row.ticker ?? ""), securityId: Number(row.securityId ?? row.security_id ?? 0) }));
        })
        .catch(() => {
            inflight = null; // let a later mount retry
            return [];
        });
    return inflight;
};

/**
 * Ticker and security id (the list's `securityId` is the stock page's `security_id`).
 * Not the exchange: the list says "Nasdaq" where stock pages say "NasdaqGS". Not the
 * ISIN: it changes on a split (MSTR's did) and the two sides can carry different ones.
 * The ticker picks the listing within the security.
 */
export const isFreeToday = (stock: StockRef | undefined, free: FreeStock[]): boolean => {
    if (!stock?.security_id) return false;
    const ticker = stock.ticker.toUpperCase();
    return free.some((f) => f.securityId === stock.security_id && f.ticker.toUpperCase() === ticker);
};
